'use client';

import { useQueries } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useCallback, useMemo, useState } from 'react';

import { ReviewCard } from '@ui/components/features/review/reviewCard';
import StarOutline from '@ui/components/icons/StarOutline';
import { cn } from '@ui/lib/utils';

import { type ReviewItem, type ReviewStatus, getReviewsByActivityId } from '../../../../../services/reviews.service';

interface ProfessionalReviewsSectionProps {
  activityIds: string[];
}

type StarFilter = 'all' | 'recent' | 1 | 2 | 3 | 4 | 5;

function formatReviewDate(timestamp: number): string {
  return new Date(timestamp).toLocaleDateString('en-US', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
}

export default function ProfessionalReviewsSection({ activityIds }: ProfessionalReviewsSectionProps) {
  const t = useTranslations('ProfessionalDetail');
  const [activeFilter, setActiveFilter] = useState<StarFilter>('all');

  const reviewQueries = useQueries({
    queries: activityIds.map((id) => ({
      queryKey: ['reviews', id, { limit: 50, status: 'approved' as const }] as const,
      queryFn: () => getReviewsByActivityId(id, { limit: 50, status: 'approved' as ReviewStatus }),
      staleTime: 5 * 60 * 1000,
      enabled: !!id
    }))
  });

  const allReviews: ReviewItem[] = useMemo(
    () => reviewQueries.flatMap((q) => q.data?.reviews ?? []).sort((a, b) => b.createdAt - a.createdAt),
    [reviewQueries]
  );

  const filteredReviews = useMemo(() => {
    if (activeFilter === 'all' || activeFilter === 'recent') return allReviews;
    return allReviews.filter((r) => r.rating === activeFilter);
  }, [allReviews, activeFilter]);

  const isLoading = reviewQueries.some((q) => q.isLoading);

  const handleFilterClick = useCallback((filter: StarFilter) => {
    setActiveFilter(filter);
  }, []);

  const filters: { value: StarFilter; label: string }[] = useMemo(
    () => [
      { value: 'all', label: t('filterAll') },
      { value: 'recent', label: t('filterRecent') },
      { value: 5, label: '5' },
      { value: 4, label: '4' },
      { value: 3, label: '3' },
      { value: 2, label: '2' },
      { value: 1, label: '1' }
    ],
    [t]
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-2 overflow-x-auto pb-1">
        {filters.map((f) => {
          const isActive = activeFilter === f.value;
          return (
            <button
              key={f.value}
              type="button"
              onClick={() => handleFilterClick(f.value)}
              className={cn(
                'cursor-pointer group inline-flex items-center gap-1.5 h-9 px-3 py-2 rounded-lg border text-sm shrink-0 transition-colors',
                isActive
                  ? 'bg-primary-50 border-primary text-primary'
                  : 'bg-white border-neutral-200 text-neutral-700 hover:border-neutral-300'
              )}
            >
              {f.label}
              {typeof f.value === 'number' && (
                <StarOutline
                  className={cn(
                    'w-3.5 h-3.5',
                    isActive ? 'text-[#FDC812]' : 'text-neutral-300 group-hover:text-[#FDC812]'
                  )}
                />
              )}
            </button>
          );
        })}
      </div>

      {isLoading && (
        <div className="flex justify-center py-8" role="status" aria-live="polite" aria-label={t('loadingReviews')}>
          <Loader2 className="h-6 w-6 animate-spin text-primary" aria-hidden />
        </div>
      )}

      {!isLoading && filteredReviews.length === 0 && (
        <p className="text-sm text-neutral-500 py-4">{t('noReviewsFound')}</p>
      )}

      {filteredReviews.length > 0 && (
        <div className="flex flex-col gap-4 divide-y divide-neutral-200">
          {filteredReviews.map((review) => (
            <ReviewCard
              key={review.id}
              userName={review.userName}
              userAvatarUrl={review.userAvatarUrl}
              date={formatReviewDate(review.createdAt)}
              rating={review.rating}
              comment={review.comment}
              tags={review.tags}
              userLikedLabel={t('userLiked')}
              seeMoreLabel={t('seeMore')}
              seeLessLabel={t('seeLess')}
              className="pt-4 first:pt-0"
            />
          ))}
        </div>
      )}
    </div>
  );
}
